import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Zap, Wand2, Save } from "lucide-react";
import { trUpper } from "../lib/mw";
import CardCapture, { readCard } from "../components/CardCapture";
import ScanCardsModal from "../components/ScanCardsModal";

const EMPTY = { firma: "", yetkili: "", gsm: "", s_tel: "", email: "", adres: "", il: "", ilce: "", subesi: "" };

export default function QuickAdd() {
  const { columns } = useAuth();
  const [raw, setRaw] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [parsing, setParsing] = useState(false);

  const subesiOptions = (columns.find((c) => c.key === "subesi")?.options) || ["ANADOLU", "AVRUPA", "DİĞER İLLER", "MERKEZ ŞUBE"];

  useEffect(() => { api.get("/geo/provinces", { silent: true }).then((r) => setProvinces(r.data)); }, []);

  const loadDistricts = async (il) => {
    if (!il) { setDistricts([]); return; }
    const { data } = await api.get(`/geo/districts/${encodeURIComponent(il)}`, { silent: true });
    setDistricts(data);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const parse = async () => {
    if (!raw.trim()) { toast.error("ÖNCE BİLGİLERİ YAPIŞTIRIN"); return; }
    setParsing(true);
    try {
      const { data } = await api.post("/parse/firma", { text: raw });
      setForm({ ...EMPTY, ...data });
      if (data.il) loadDistricts(data.il);
      toast.success("BİLGİLER AYRIŞTIRILDI — KONTROL EDİP EKLEYİN");
    } catch (e) {
      toast.error("AYRIŞTIRMA HATASI");
    } finally {
      setParsing(false);
    }
  };

  const onProvince = (v) => { setForm((f) => ({ ...f, il: v, ilce: "" })); loadDistricts(v); };

  const add = async () => {
    if (!form.firma) { toast.error("FİRMA ADI GEREKLİ"); return; }
    await api.post("/customers", { data: form });
    toast.success("FİRMA EKLENDİ");
    setRaw(""); setForm(EMPTY); setDistricts([]);
  };

  const ta = "min-h-[72px] border-slate-700 bg-slate-800 text-white";
  const [pasting, setPasting] = useState(false);
  const [bulk, setBulk] = useState(false);
  const onCard = (d) => { setForm({ ...EMPTY, ...d }); if (d.il) loadDistricts(d.il); };
  const inp = "h-10 border-slate-700 bg-slate-800 text-white";
  const sel = "h-10 w-full rounded-md border border-slate-700 bg-slate-800 px-3 text-sm text-white";

  return (
    <div className="mw-fade-up space-y-5">
      <div>
        <h1 className="flex items-center gap-2 font-head text-2xl md:text-3xl font-bold tracking-tight text-white"><Zap className="h-7 w-7 text-amber-400" /> HIZLI FİRMA EKLE</h1>
        <p className="text-sm text-slate-400">İNTERNETTEN KOPYALADIĞINIZ BİLGİLERİ YAPIŞTIRIN, SİSTEM FORMATA OTURTSUN</p>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="space-y-3 rounded-xl border border-slate-800 bg-slate-900 p-3 md:p-5">
          <CardCapture onData={onCard} />
          <Button data-testid="quick-bulk-paste" onClick={() => setBulk(true)} className="w-full bg-indigo-900 text-indigo-100 hover:bg-indigo-800">TOPLU EKRAN GÖRÜNTÜSÜ YAPIŞTIR — HEPSİNİ BİRDEN EKLE</Button>
          <ScanCardsModal open={bulk} onOpenChange={setBulk} onDone={() => toast.success("FİRMALAR MÜŞTERİ LİSTESİNE EKLENDİ")} />
          <Label className="text-xs text-slate-400">VEYA YAPIŞTIRILAN BİLGİLER YA DA EKRAN GÖRÜNTÜSÜ (CTRL+V) — YATAY / DİKEY FARK ETMEZ</Label>
          <Textarea
            data-testid="quick-raw-input"
            value={pasting ? "EKRAN GÖRÜNTÜSÜ OKUNUYOR..." : raw}
            disabled={pasting}
            onChange={(e) => setRaw(e.target.value)}
            onPaste={async (e) => {
              const img = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/"));
              if (!img) return;
              e.preventDefault();
              setPasting(true); await readCard(img.getAsFile(), onCard); setPasting(false);
            }}
            placeholder={"ÖRN:\nYILDIZ LOJİSTİK LTD. ŞTİ.\nAhmet Yıldız\n0212 555 11 22\n0532 111 22 33\ninfo@yildiz.com\nMerkez Mah. Atatürk Cad. No:5 Kadıköy / İstanbul"}
            className="min-h-[160px] md:min-h-[320px] border-slate-700 bg-slate-800 font-mono text-sm text-white"
          />
          <Button data-testid="quick-parse-button" onClick={parse} disabled={parsing} className="w-full bg-amber-600 text-white hover:bg-amber-700">
            <Wand2 className="mr-1 h-4 w-4" /> {parsing ? "AYRIŞTIRILIYOR..." : "TARA / AYRIŞTIR"}
          </Button>
          <p className="text-[11px] leading-relaxed text-slate-500">
            GSM'LER (05xx) GSM SÜTUNUNA, SABİT TELLER S.TEL'E, E-POSTALAR EMAIL'E ALT ALTA YAZILIR. İL/İLÇE OTOMATİK BULUNUR. İSTANBUL AVRUPA İLÇELERİ → <b>AVRUPA</b>, ANADOLU İLÇELERİ → <b>ANADOLU</b>, DİĞER İLLER → İL ADI ŞUBE OLUR. İSTERSENİZ ŞUBEYİ DEĞİŞTİREBİLİR VEYA <b>DİĞER İLLER</b> ATAYABİLİRSİNİZ.
          </p>
        </div>

        <div className="space-y-4 rounded-xl border border-slate-800 bg-slate-900 p-3 md:p-5">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">FİRMA ADI</Label>
            <Input data-testid="quick-firma" className={inp} value={form.firma} onChange={(e) => set("firma", trUpper(e.target.value))} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">YETKİLİ(LER)</Label>
              <Textarea data-testid="quick-yetkili" className={ta} value={form.yetkili} onChange={(e) => set("yetkili", trUpper(e.target.value))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">ADRES</Label>
              <Textarea data-testid="quick-adres" className={ta} value={form.adres} onChange={(e) => set("adres", trUpper(e.target.value))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">GSM (ALT ALTA)</Label>
              <Textarea data-testid="quick-gsm" className={ta} value={form.gsm} onChange={(e) => set("gsm", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">S.TEL (ALT ALTA)</Label>
              <Textarea data-testid="quick-stel" className={ta} value={form.s_tel} onChange={(e) => set("s_tel", e.target.value)} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs text-slate-400">EMAIL (ALT ALTA)</Label>
              <Textarea data-testid="quick-email" className={ta} value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">İL</Label>
              <select data-testid="quick-il" className={sel} value={form.il} onChange={(e) => onProvince(e.target.value)}>
                <option value="">— İL —</option>
                {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">İLÇE</Label>
              <select data-testid="quick-ilce" className={sel} value={form.ilce} onChange={(e) => set("ilce", e.target.value)}>
                <option value={form.ilce || ""}>{form.ilce || "— İLÇE —"}</option>
                {districts.filter((d) => d !== form.ilce).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs text-slate-400">MÜŞTERİ ŞUBESİ</Label>
              <select data-testid="quick-subesi" className={sel} value={form.subesi} onChange={(e) => set("subesi", e.target.value)}>
                <option value="">— ŞUBE —</option>
                {subesiOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <Button data-testid="quick-add-button" onClick={add} className="w-full bg-blue-600 text-white hover:bg-blue-700">
            <Save className="mr-1 h-4 w-4" /> FİRMAYI EKLE
          </Button>
        </div>
      </div>
    </div>
  );
}
